export const SHIFT_LABELS = { A: '07:00–15:00', B: '15:00–23:00', C: '23:00–07:00 (+1 day)', OC: 'Friday off', V: 'Vacation', '-': 'Unassigned' };
export const SHIFT_COLORS = { A: 'DBEAFE', B: 'FEF3C7', C: 'EDE9FE', OC: 'E2E8F0', V: 'DCFCE7', '-': 'FFFFFF' };
export const SHIFT_CLASSES = {
  A: 'bg-info-soft text-info-fg', B: 'bg-warning-soft text-warning-fg', C: 'bg-violet-100 text-violet-900',
  OC: 'bg-muted text-muted-fg', V: 'bg-success-soft text-success-fg', '-': 'bg-surface text-muted-fg'
};
export const PROJECT_BACKGROUNDS = {
  FM008: 'bg-[#f3f8fc] [--project-accent:#6085a6]',
  FM014: 'bg-[#f2f8f6] [--project-accent:#628c7d]',
  FM020: 'bg-[#fcf8f2] [--project-accent:#b29365]',
  FM030: 'bg-[#f7f5fb] [--project-accent:#8c7aaa]'
};
export const projectBackground = (project) => {
  const number = Number(String(project || '').replace(/[^0-9]/g, ''));
  return PROJECT_BACKGROUNDS[`FM${String(number).padStart(3, '0')}`] || 'bg-surface';
};
const staffTypes = ['manager', 'engineer', 'supervisor', 'technician'];
const staffRank = (designation) => {
  const index = staffTypes.indexOf(String(designation || '').trim().toLowerCase());
  return index < 0 ? staffTypes.length : index;
};
const projectSortKey = (project) => {
  const value = String(project || '').trim();
  const match = value.match(/^FM\s*0*(\d+)$/i);
  return match ? `FM${match[1].padStart(3, '0')}` : value;
};
export const sortRosterRows = (rows) => [...rows].sort((a, b) =>
  projectSortKey(a.project).localeCompare(projectSortKey(b.project), 'en', { numeric: true, sensitivity: 'base' }) ||
  staffRank(a.designation) - staffRank(b.designation) ||
  String(a.name || '').localeCompare(String(b.name || ''), 'en', { sensitivity: 'base' }) ||
  String(a.userId).localeCompare(String(b.userId))
);
export const SCHEDULE_PRESETS = ['A', 'B', 'C', 'A/B', 'A/B/C'];
export const schedulePreset = (profile) => profile.schedulingMode !== 'custom' && SCHEDULE_PRESETS.includes(profile.pattern.join('/')) ? profile.pattern.join('/') : 'custom';
export const scheduleLabel = (profile) => `${schedulePreset(profile) === 'custom' ? 'Custom' : 'Fixed'} ${profile.pattern.join('/')}`;
export const currentMonth = () => {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Qatar', year: 'numeric', month: '2-digit' }).formatToParts(new Date());
  return `${parts.find((p) => p.type === 'year').value}-${parts.find((p) => p.type === 'month').value}`;
};
export const anchorForMonth = (month) => {
  const date = new Date(`${month}-01T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() - ((date.getUTCDay() + 1) % 7));
  return date.toISOString().slice(0, 10);
};
export const monthLabel = (month) => new Date(`${month}-01T00:00:00Z`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
export const weekday = (date) => new Date(`${date}T00:00:00Z`).toLocaleDateString('en-GB', { weekday: 'short', timeZone: 'UTC' });
export const rosterWeekStart = (date) => {
  const value = new Date(`${date}T00:00:00Z`);
  value.setUTCDate(value.getUTCDate() - ((value.getUTCDay() + 1) % 7));
  return value.toISOString().slice(0, 10);
};
export const rosterWeeks = (dates) => [...new Set(dates.map(rosterWeekStart))].map(start => ({ start, dates: dates.filter(date => rosterWeekStart(date) === start) }));
export const hasWeeklyAssignments = (roster) => Array.isArray(roster?.rows) && roster.rows.length > 0 && roster.rows.every(row => {
  if (!Array.isArray(row.days) || !row.days.length || !Array.isArray(row.weeks)) return false;
  const starts = rosterWeeks(row.days.map(day => day.date)).map(week => week.start);
  return row.weeks.length === starts.length && starts.every(start => row.weeks.some(week =>
    week?.start === start && ['A', 'B', 'C'].includes(week.shift) &&
    ['A', 'B', 'C'].includes(week.generatedShift) && typeof week.manual === 'boolean' &&
    (week.manual || week.shift === week.generatedShift)));
});
export const coveragePool = (project) => {
  const number = Number(String(project || '').replace(/[^0-9]/g, ''));
  return number === 8 ? 'FM008' : [20, 30].includes(number) ? 'FM020 + FM030' : null;
};
export const weeklyCoverage = (rows) => {
  const counts = new Map();
  rows.forEach(row => row.days.forEach(day => {
    const pool = coveragePool(row.project);
    if (!pool || weekday(day.date) === 'Fri') return;
    const key = `${pool}:${day.date}`;
    if (!counts.has(key)) counts.set(key, { pool, date: day.date, A: 0, B: 0, C: 0 });
    if (['A', 'B', 'C'].includes(day.shift)) counts.get(key)[day.shift]++;
  }));
  const groups = new Map();
  counts.forEach(day => {
    const start = rosterWeekStart(day.date), key = `${day.pool}:${start}`;
    if (!groups.has(key)) groups.set(key, { pool: day.pool, start, days: [] });
    groups.get(key).days.push(day);
  });
  return [...groups.values()].sort((a,b) => a.start.localeCompare(b.start) || a.pool.localeCompare(b.pool));
};
export const editRosterWeek = (roster, userId, start, code) => ({
  ...roster,
  rows: roster.rows.map(row => {
    if (row.userId !== userId) return row;
    const prior = row.weeks?.find(week => week.start === start);
    if (!prior) return row;
    const week = { ...prior, shift: code === 'AUTO' ? prior.generatedShift : code, manual: code !== 'AUTO' };
    return { ...row, weeks: row.weeks.map(entry => entry.start === start ? week : entry), days: row.days.map(day =>
      rosterWeekStart(day.date) !== start || ['OC', 'V'].includes(day.shift) ? day : { ...day, shift: week.shift, generatedShift: week.generatedShift, manual: week.manual }) };
  })
});
